// ONE document-level listener that computes the accessible label once (innerText forces layout) and fans out to sinks.
// NEVER captures keystrokes or input values — only the clicked control's label.

import { CLIENT_TELEMETRY_CAPS, redactTelemetryText } from 'shared/wide-event-kinds'

export const INTERACTIVE_SELECTOR =
  'button, a, select, [role="button"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="checkbox"], input[type="checkbox"], input[type="radio"], input[type="submit"]'

const redactLabel = (text: string | null | undefined): string =>
  redactTelemetryText(text ?? '', CLIENT_TELEMETRY_CAPS.label)

/** A field's NAME — never its value: `name`, then `aria-label`, then its <label>'s text, then its id. */
export function fieldName(field: {
  name?: string | null
  ariaLabel?: string | null
  labelText?: string | null
  id?: string | null
}): string {
  return (
    redactLabel(field.name) ||
    redactLabel(field.ariaLabel) ||
    redactLabel(field.labelText) ||
    redactLabel(field.id) ||
    'field'
  )
}

// What these show is a VALUE (the picked option, an employee's or a job's name), so they are named like a field.
const VALUE_ROLES = new Set(['combobox', 'listbox', 'option', 'textbox', 'searchbox', 'spinbutton'])

function fieldOf(el: Element): string {
  const owner = elementRole(el) === 'option' ? (el.closest('select, [role="listbox"]') ?? el) : el
  const labels = (owner as HTMLSelectElement).labels as NodeListOf<HTMLLabelElement> | undefined
  return fieldName({
    name: owner.getAttribute('name'),
    ariaLabel: owner.getAttribute('aria-label'),
    labelText: labels?.[0]?.innerText ?? labels?.[0]?.textContent,
    id: owner.id,
  })
}

// A capitalised word past a sentence's first is a proper name in sentence-case copy (`Hide Ana López from…`), and
// quotes wrap what a person wrote. All-caps words (`JSON`, `API`) are UI.
const NAME_LIKE = /^\p{Lu}\p{Ll}/u
const QUOTED = /["“”«»‘’]/

/** Text that reads as UI copy, redacted and capped; '' when it could carry a name or quoted user text. Pure. */
export function staticText(text: string | null | undefined): string {
  const redacted = redactLabel(text)
  if (!redacted || QUOTED.test(redacted)) return ''
  const words = redacted.split(' ')
  const named = words.some((word, i) => i > 0 && !(words[i - 1] ?? '').endsWith('.') && NAME_LIKE.test(word))
  return named ? '' : redacted
}

/**
 * The clicked control's label, never its value: an explicit `data-telemetry-label`; a select, an option or a
 * combobox by its field's name; else the first of its visible text, aria-label and title that reads as UI copy;
 * else its role. Visible text leads because an aria-label is where a name gets interpolated (`Show ${name} again`).
 * The rule: docs/notes/server/wide-events.md, "Labels".
 */
export function elementLabel(el: Element): string {
  const override = el.getAttribute('data-telemetry-label')
  if (override) return redactLabel(override) || elementRole(el)
  if (VALUE_ROLES.has(elementRole(el))) return fieldOf(el)
  return (
    staticText((el as HTMLElement).innerText ?? el.textContent) ||
    staticText(el.getAttribute('aria-label')) ||
    staticText(el.getAttribute('title')) ||
    elementRole(el)
  )
}

const INPUT_ROLES: Record<string, string> = {
  checkbox: 'checkbox',
  radio: 'radio',
  submit: 'button',
  button: 'button',
  reset: 'button',
  range: 'slider',
  search: 'searchbox',
}
const TAG_ROLES: Record<string, string> = {
  button: 'button',
  select: 'combobox',
  textarea: 'textbox',
  form: 'form',
  summary: 'button',
}

/** The ARIA role a control exposes: its explicit `role`, else the implicit one of its tag. Pure. */
export function controlRole(control: {
  tag: string
  role?: string | null
  type?: string | null
  href?: boolean
}): string {
  if (control.role) return control.role.split(' ')[0] ?? control.role
  const tag = control.tag.toLowerCase()
  if (tag === 'a') return control.href ? 'link' : 'generic'
  if (tag === 'input') return INPUT_ROLES[(control.type ?? 'text').toLowerCase()] ?? 'textbox'
  return TAG_ROLES[tag] ?? tag
}

export const elementRole = (el: Element): string =>
  controlRole({
    tag: el.tagName,
    role: el.getAttribute('role'),
    type: el.getAttribute('type'),
    href: el.hasAttribute('href'),
  })

type ClickSink = (label: string, control: Element, event: MouseEvent) => void

const sinks = new Set<ClickSink>()
let listenerInstalled = false

// The shared listener is installed lazily on first registration; registering the same sink twice is a no-op.
export function registerClickSink(sink: ClickSink): void {
  if (typeof window === 'undefined') return
  sinks.add(sink)
  if (listenerInstalled) return
  listenerInstalled = true

  document.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target
        if (!(target instanceof Element)) return
        // A control hidden from the a11y tree is never the one the user believes they clicked, so resolve from its parent.
        // The iOS haptic switches (route-transition/haptics.ts) are laid invisibly over every section link, and without
        // this every iOS nav landed in the trail as a click on "input" instead of on "Jobs".
        const decorative = target.closest('[aria-hidden="true"]')
        const control = (decorative ? decorative.parentElement : target)?.closest(INTERACTIVE_SELECTOR)
        if (!control) return
        const label = elementLabel(control)
        for (const sinkFn of sinks) {
          try {
            sinkFn(label, control, event)
          } catch {
            // One sink failing must not starve the others — and never break a real click.
          }
        }
      } catch {
        // Never let telemetry break a real click.
      }
    },
    true,
  )
}
